// References and search on the screen: what a `[[kind:id|name]]` points to now,
// and the screen's search (`/api/search`). Every query key of the refs part's
// screen side lives here.
//
// Promise:
// - One reference is one query (`refQuery`), so a rename drops only the answers
//   about that thing (`refPrefix(kind, id)`, from the owning part's realtime
//   handlers), however many chips show it.
// - A text with many chips still sends one request: the references asked for
//   within one window of BATCH_MS go to the server together
//   (`/api/refs/resolve`, up to MAX_PER_REQUEST each). The window lives in the
//   query function, not in an effect.
// - Every reference put in a window is settled, once, whatever happens: each
//   one sets its own timer (the first to fire sends them all), and one left
//   over by an unexpected failure is failed rather than left waiting.
// - The window is per API (a WeakMap), never shared between apps or tests.
// - What the viewer may not see answers like what is not there
//   (`visible: false`). `known` says whether the app has the kind at all.
import { type QueryClient, queryOptions } from "@tanstack/react-query";
import type { Api } from "../data/api";

export const BATCH_MS = 150;
export const MAX_PER_REQUEST = 100;

/** What a reference asks for: by id (written in a text) or by key (a pasted URL). */
export type RefRequest =
  | { kind: string; id: string }
  | { kind: string; key: string };

export type ResolvedRef =
  | {
      visible: true;
      kind: string;
      id: string;
      label: string;
      location?: readonly string[];
      path?: Readonly<Record<string, unknown>>;
    }
  /** `unreachable`: where it lives is out of reach now (a Hub's cloud). */
  | { visible: false; known: boolean; unreachable?: boolean };

// ---- the window ----------------------------------------------------------------

interface Waiting {
  req: RefRequest;
  resolve(ref: ResolvedRef): void;
  reject(error: unknown): void;
}

const windows = new WeakMap<Api, Waiting[]>();

function enqueue(api: Api, req: RefRequest): Promise<ResolvedRef> {
  return new Promise<ResolvedRef>((resolve, reject) => {
    let waiting = windows.get(api);
    if (!waiting) {
      waiting = [];
      windows.set(api, waiting);
    }
    waiting.push({ req, resolve, reject });
    setTimeout(() => void flush(api), BATCH_MS);
  });
}

async function flush(api: Api): Promise<void> {
  const waiting = windows.get(api);
  // Taken out first: what comes later opens the next window, and the timers of
  // this window that fire later find nothing.
  windows.delete(api);
  if (!waiting || waiting.length === 0) return;
  const undecided = new Set(waiting);
  try {
    for (let at = 0; at < waiting.length; at += MAX_PER_REQUEST) {
      const chunk = waiting.slice(at, at + MAX_PER_REQUEST);
      try {
        const answer = await api<{ refs: ResolvedRef[] }>("/api/refs/resolve", {
          method: "POST",
          body: { refs: chunk.map((w) => w.req) },
        });
        for (const [i, w] of chunk.entries()) {
          undecided.delete(w);
          w.resolve(answer?.refs?.[i] ?? { visible: false, known: false });
        }
      } catch (error) {
        // Each reference fails on its own; its chip keeps the name as written.
        for (const w of chunk) {
          undecided.delete(w);
          w.reject(error);
        }
      }
    }
  } finally {
    for (const w of undecided) w.reject(new Error("refs: window aborted"));
  }
}

// ---- queries -------------------------------------------------------------------

export const refsPrefix = ["me", "refs"] as const;
export const refPrefix = (kind: string, id?: string) =>
  id === undefined ? [...refsPrefix, kind] : [...refsPrefix, kind, id];

/** One reference by id. Changes come through the owning part's events. */
export const refQuery = (api: Api, kind: string, id: string) =>
  queryOptions({
    queryKey: [...refsPrefix, kind, id] as const,
    queryFn: () => enqueue(api, { kind, id }),
    staleTime: 5 * 60_000,
    retry: false,
  });

/**
 * A reference by key (a pasted URL). What it resolves to is also written under
 * the id's own key, so later reads by id and renames find it.
 */
export const refByKeyQuery = (
  api: Api,
  qc: QueryClient,
  req: { kind: string; key: string },
) =>
  queryOptions({
    queryKey: [...refsPrefix, req.kind, "by-key", req.key] as const,
    queryFn: async (): Promise<ResolvedRef> => {
      const ref = await enqueue(api, req);
      if (ref.visible) qc.setQueryData([...refsPrefix, ref.kind, ref.id], ref);
      return ref;
    },
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

/** Resolves one reference outside React (a paste). */
export function fetchResolvedRef(
  qc: QueryClient,
  api: Api,
  req: RefRequest,
): Promise<ResolvedRef> {
  if ("id" in req) return qc.fetchQuery(refQuery(api, req.kind, req.id));
  return qc.fetchQuery(refByKeyQuery(api, qc, req));
}

// ---- search ------------------------------------------------------------------

export interface SearchHit {
  id: string;
  kind: string;
  label: string;
  matchCount: number;
  snippets: {
    field: string;
    text: string;
    stepId?: string;
    stepTitle?: string;
    stepKind?: string;
    stepStatus?: string;
  }[];
  [key: string]: unknown;
}

export interface SearchAnswer {
  results: Record<string, SearchHit[]>;
  types: string[];
}

export const searchPrefix = ["me", "search"] as const;

export const searchQuery = (
  api: Api,
  q: string,
  types: readonly string[],
  limit = 10,
) =>
  queryOptions({
    queryKey: [...searchPrefix, types.join(","), q, limit] as const,
    queryFn: () =>
      api<SearchAnswer>(
        `/api/search?q=${encodeURIComponent(q)}&types=${encodeURIComponent(types.join(","))}&limit=${limit}`,
      ),
    enabled: q.trim() !== "",
  });
